import api, { setAccessToken } from "./client";

export interface ResidentMembership {
  resident_id: string;
  role: string;
  role_display: string;
  is_primary: boolean;
  unit: {
    id: string | null;
    unit_code: string | null;
    floor: string;
    number: string;
  };
  building: {
    id: string | null;
    name: string | null;
    code: string | null;
    organization_id?: string | null;
    hoa_admin_id?: string | null;
  };
}

export interface ResidentUserSummary {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  phone: string;
  language: string;
  has_completed_setup: boolean;
}

export interface ResidentAuthResponse {
  success: boolean;
  authenticated?: boolean;
  requires_property_selection?: boolean;
  message?: string;
  user?: ResidentUserSummary;
  active_membership?: ResidentMembership;
  memberships?: ResidentMembership[];
  access_token?: string;
  selection_token?: string;
  has_completed_setup?: boolean;
}

export interface ResidentPasswordLoginRequest {
  email: string;
  password: string;
}

export interface ResidentOtpRequest {
  email: string;
}

export interface ResidentOtpVerifyRequest {
  email: string;
  code: string;
  resident_id?: string;
}

export interface ResidentClaimRequest extends ResidentOtpVerifyRequest {
  resident_id: string;
}

export interface ResidentClaimOtpRequest {
  resident_id: string;
  email: string;
}

export interface ResidentPropertySelectionRequest {
  selection_token: string;
  resident_id: string;
}

export interface ResidentPropertySwitchRequest {
  resident_id: string;
}

export interface ResidentPasswordResetVerifyResponse {
  success: boolean;
  message: string;
  reset_token: string;
}

export interface ResidentPasswordResetConfirmRequest {
  reset_token: string;
  new_password: string;
  confirm_password: string;
}

export interface ResidentProfileResponse {
  success: boolean;
  data: {
    resident_id: string;
    full_name: string;
    email: string;
    phone: string;
    role: string;
    language: string;
    has_completed_setup: boolean;
    setup_completed_at: string | null;
    last_login: string | null;
    unit: {
      id: string;
      unit_code: string;
      floor: string;
      number: string;
    };
    building: {
      id: string;
      name: string;
      code: string;
    } | null;
    active_membership: ResidentMembership;
    memberships: ResidentMembership[];
  };
}

export interface CompleteSetupRequest {
  full_name: string;
  phone?: string;
  password: string;
  language: string;
}

export interface CompleteSetupResponse {
  success: boolean;
  message: string;
  data: {
    resident_id: string;
    full_name: string;
    email: string;
    phone: string;
    language: string;
    has_completed_setup: boolean;
    setup_completed_at: string;
  };
}

export interface PendingMembership {
  resident_id: string;
  email: string;
  role_display: string;
  building: {
    id: string;
    name: string;
  };
  unit: {
    id: string;
    unit_code: string;
  };
}

export interface PendingMembershipsResponse {
  success: boolean;
  pending_memberships: PendingMembership[];
}

const RESIDENT_ACCESS_KEY = "resident_access_token";
const RESIDENT_SELECTION_TOKEN_KEY = "resident_selection_token";
const RESIDENT_MEMBERSHIPS_KEY = "resident_memberships";
const RESIDENT_ACTIVE_MEMBERSHIP_KEY = "resident_active_membership";
const RESIDENT_NAME_KEY = "resident_name";
const RESIDENT_EMAIL_KEY = "resident_email";
const RESIDENT_ID_KEY = "resident_id";
const RESIDENT_USER_KEY = "resident_user";

export function clearResidentSessionStorage() {
  localStorage.removeItem(RESIDENT_ACCESS_KEY);
  localStorage.removeItem(RESIDENT_SELECTION_TOKEN_KEY);
  localStorage.removeItem(RESIDENT_MEMBERSHIPS_KEY);
  localStorage.removeItem(RESIDENT_ACTIVE_MEMBERSHIP_KEY);
  localStorage.removeItem(RESIDENT_NAME_KEY);
  localStorage.removeItem(RESIDENT_EMAIL_KEY);
  localStorage.removeItem(RESIDENT_ID_KEY);
  localStorage.removeItem(RESIDENT_USER_KEY);
}

function persistResidentUser(user?: ResidentUserSummary) {
  if (!user) return;
  localStorage.setItem(RESIDENT_USER_KEY, JSON.stringify(user));
  localStorage.setItem(RESIDENT_NAME_KEY, user.full_name || user.email);
  localStorage.setItem(RESIDENT_EMAIL_KEY, user.email);
}

function persistActiveMembership(membership?: ResidentMembership) {
  if (!membership) return;
  localStorage.setItem(RESIDENT_ACTIVE_MEMBERSHIP_KEY, JSON.stringify(membership));
  localStorage.setItem(RESIDENT_ID_KEY, membership.resident_id);
}

export function persistResidentSelectionState(response: ResidentAuthResponse) {
  if (!response.selection_token) return;
  localStorage.setItem(RESIDENT_SELECTION_TOKEN_KEY, response.selection_token);
  localStorage.setItem(RESIDENT_MEMBERSHIPS_KEY, JSON.stringify(response.memberships || []));
  persistResidentUser(response.user);
}

export function persistResidentAuthenticatedState(response: ResidentAuthResponse) {
  if (!response.access_token) return;
  setAccessToken(response.access_token);
  localStorage.setItem(RESIDENT_ACCESS_KEY, response.access_token);
  localStorage.removeItem(RESIDENT_SELECTION_TOKEN_KEY);
  localStorage.setItem(RESIDENT_MEMBERSHIPS_KEY, JSON.stringify(response.memberships || []));
  persistResidentUser(response.user);
  persistActiveMembership(response.active_membership);
}

export function getStoredResidentSelectionToken(): string | null {
  return localStorage.getItem(RESIDENT_SELECTION_TOKEN_KEY);
}

export function getStoredResidentMemberships(): ResidentMembership[] {
  const raw = localStorage.getItem(RESIDENT_MEMBERSHIPS_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as ResidentMembership[];
  } catch {
    return [];
  }
}

export function getStoredActiveMembership(): ResidentMembership | null {
  const raw = localStorage.getItem(RESIDENT_ACTIVE_MEMBERSHIP_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ResidentMembership;
  } catch {
    return null;
  }
}

export async function loginResidentWithPassword(
  data: ResidentPasswordLoginRequest
): Promise<ResidentAuthResponse> {
  const response = await api.post<ResidentAuthResponse>("/auth/resident/login/", data);
  return response.data;
}

export async function requestResidentLoginOtp(data: ResidentOtpRequest): Promise<{ success: boolean; message: string; resend_allowed_at?: string }> {
  const response = await api.post<{ success: boolean; message: string; resend_allowed_at?: string }>("/auth/resident/otp/request/", data);
  return response.data;
}

export async function verifyResidentLoginOtp(data: ResidentOtpVerifyRequest): Promise<ResidentAuthResponse> {
  const response = await api.post<ResidentAuthResponse>("/auth/resident/otp/verify/", data);
  return response.data;
}

export async function claimResidentMembership(data: ResidentClaimRequest): Promise<ResidentAuthResponse> {
  const response = await api.post<ResidentAuthResponse>("/auth/resident/claim/", data);
  return response.data;
}

export async function requestResidentClaimOtp(
  data: ResidentClaimOtpRequest
): Promise<{ success: boolean; message: string; resend_allowed_at?: string }> {
  const response = await api.post<{ success: boolean; message: string; resend_allowed_at?: string }>(
    "/auth/resident/claim/request/",
    data
  );
  return response.data;
}

export async function getResidentProperties(): Promise<{ success: boolean; user: ResidentUserSummary; memberships: ResidentMembership[] }> {
  const response = await api.get<{ success: boolean; user: ResidentUserSummary; memberships: ResidentMembership[] }>(
    "/auth/resident/properties/"
  );
  return response.data;
}

export async function selectResidentProperty(
  data: ResidentPropertySelectionRequest
): Promise<ResidentAuthResponse> {
  const response = await api.post<ResidentAuthResponse>("/auth/resident/select-property/", data);
  return response.data;
}

export async function switchResidentProperty(
  data: ResidentPropertySwitchRequest
): Promise<ResidentAuthResponse> {
  const response = await api.post<ResidentAuthResponse>("/auth/resident/switch-property/", data);
  return response.data;
}

export async function requestResidentPasswordReset(data: ResidentOtpRequest): Promise<{ success: boolean; message: string }> {
  const response = await api.post<{ success: boolean; message: string }>(
    "/auth/resident/password/reset/request/",
    data
  );
  return response.data;
}

export async function verifyResidentPasswordReset(
  data: ResidentOtpVerifyRequest
): Promise<ResidentPasswordResetVerifyResponse> {
  const response = await api.post<ResidentPasswordResetVerifyResponse>(
    "/auth/resident/password/reset/verify/",
    data
  );
  return response.data;
}

export async function confirmResidentPasswordReset(
  data: ResidentPasswordResetConfirmRequest
): Promise<{ success: boolean; message: string }> {
  const response = await api.post<{ success: boolean; message: string }>(
    "/auth/resident/password/reset/confirm/",
    data
  );
  return response.data;
}

export async function getResidentProfile(): Promise<ResidentProfileResponse> {
  const response = await api.get("/building/residents/profile/get_profile/");
  return response.data;
}

export async function completeProfileSetup(data: CompleteSetupRequest): Promise<CompleteSetupResponse> {
  const response = await api.post("/building/residents/profile/complete_setup/", data);
  return response.data;
}

export async function updateResidentProfile(data: Partial<CompleteSetupRequest>): Promise<CompleteSetupResponse> {
  const response = await api.put("/building/residents/profile/update_profile/", data);
  return response.data;
}

export async function getPendingResidentMemberships(): Promise<PendingMembershipsResponse> {
  const response = await api.get<PendingMembershipsResponse>("/auth/resident/pending-memberships/");
  return response.data;
}

export async function acceptResidentInvitation(
  residentId: string
): Promise<{ success: boolean; message: string; membership?: object }> {
  const response = await api.post<{ success: boolean; message: string; membership?: object }>(
    "/auth/resident/claim/accept/",
    { resident_id: residentId }
  );
  return response.data;
}

// Backward-compatible alias for legacy imports.
export const loginWithOTP = claimResidentMembership;
